import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UnterweisungenLayout } from './UnterweisungenLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ResendInstructionModal } from '../../components/ResendInstructionModal';
import { InstructionSignModal } from '../../components/InstructionSignModal';
import { ChevronLeft, ChevronDown, ChevronRight, FileText, Check, X, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function for deadline status
const getDeadlineStatus = (deadline, isConfirmed) => {
  if (isConfirmed) {
    return { color: 'green', label: 'Gelesen', icon: '✅' };
  }
  
  if (!deadline) {
    return { color: 'gray', label: 'Offen', icon: '⏳' };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { color: 'red', label: 'Überfällig', icon: '🔴' };
  } else if (diffDays <= 1) {
    return { color: 'orange', label: 'Dringend', icon: '🟠' };
  } else {
    return { color: 'gray', label: 'Offen', icon: '⏳' };
  }
};

export const KategorieDetail = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [expandedInstructions, setExpandedInstructions] = useState([]);
  const [resendModal, setResendModal] = useState(null);
  const [slSignModal, setSlSignModal] = useState(null);
  const [slName, setSlName] = useState('');

  useEffect(() => {
    fetchData();
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const p = JSON.parse(atob(token.split('.')[1]));
        setSlName(p.name || p.sub || '');
      } catch { /* silent */ }
    }
  }, [categoryName]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Get instructions for this category
      const [instructionsRes, trackingRes] = await Promise.all([
        axios.get(`${API}/api/instructions?category=${encodeURIComponent(categoryName)}`, { headers }),
        axios.get(`${API}/api/instructions/tracking`, { headers })
      ]);
      
      setInstructions(instructionsRes.data || []);
      setTrackingData(trackingRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const toggleInstruction = (instructionId) => {
    setExpandedInstructions(prev =>
      prev.includes(instructionId)
        ? prev.filter(id => id !== instructionId)
        : [...prev, instructionId]
    );
  };

  // Group tracking data by instruction
  const getTrackingForInstruction = (instructionId) => {
    return trackingData.filter(t => t.instruction_id === instructionId);
  };

  if (loading) {
    return (
      <UnterweisungenLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </UnterweisungenLayout>
    );
  }

  return (
    <UnterweisungenLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Breadcrumb & Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/unterweisungen/kategorien')}
            className="mb-3 sm:mb-4 p-0 h-auto text-sm sm:text-base hover:bg-transparent"
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-1" />
            Zurück zu Kategorien
          </Button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">{categoryName}</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {instructions.length} Unterweisung{instructions.length !== 1 ? 'en' : ''}
          </p>
        </div>

        {/* Instructions List */}
        {instructions.length === 0 ? (
          <Card>
            <CardContent className="py-12 sm:py-16">
              <div className="text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Keine Unterweisungen in dieser Kategorie</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {instructions.map(instruction => {
              const isExpanded = expandedInstructions.includes(instruction.id);
              const tracking = getTrackingForInstruction(instruction.id);
              const confirmedCount = tracking.filter(t => t.is_confirmed).length;
              const totalCount = tracking.length;
              const rate = totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0;
              
              return (
                <Card key={instruction.id} className="overflow-hidden">
                  {/* Instruction Header - Clickable */}
                  <button
                    onClick={() => toggleInstruction(instruction.id)}
                    className="w-full text-left hover:bg-gray-50 dark:bg-gray-800 transition-colors"
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-3">
                        {/* Left: Icon + Info */}
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                            <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
                              {instruction.title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {/* Status Badge */}
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                rate === 100 ? 'bg-green-100 text-green-700' :
                                rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {confirmedCount}/{totalCount} gelesen
                              </span>
                              
                              {/* Deadline Badge */}
                              {instruction.deadline && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  📅 {new Date(instruction.deadline).toLocaleDateString('de-DE')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: Action Buttons + Rate + Expand Icon */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Re-send Button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setResendModal(instruction);
                            }}
                            className="h-8 px-2 text-xs"
                            title="Erneut versenden"
                          >
                            <Send className="h-4 w-4 text-green-600" />
                            <span className="hidden sm:inline ml-1">Versenden</span>
                          </Button>
                          
                          <div className="text-right hidden sm:block">
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{rate}%</div>
                            <div className="text-xs text-gray-500">{confirmedCount}/{totalCount}</div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </button>

                  {/* Expanded: Employee List */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      {tracking.length === 0 ? (
                        <div className="p-4 sm:p-6 text-center text-gray-500 text-sm">
                          Keine Mitarbeiter zugewiesen
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {tracking.map(item => {
                            const status = getDeadlineStatus(item.deadline, item.is_confirmed);
                            
                            return (
                              <div key={`${item.instruction_id}_${item.employee_id}`} className="p-3 sm:p-4 hover:bg-white dark:bg-gray-900 transition-colors">
                                <div className="flex items-center justify-between gap-3">
                                  {/* Employee Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
                                      {item.employee_name}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                      {item.employee_email}
                                    </div>
                                  </div>

                                  {/* Status */}
                                  <div className="text-right flex-shrink-0">
                                    {item.is_confirmed ? (
                                      <div className="flex items-center gap-2">
                                        <div className="text-right hidden sm:block">
                                          <div className="text-xs font-medium text-green-600">Gelesen</div>
                                          <div className="text-xs text-gray-500">
                                            {new Date(item.confirmed_at).toLocaleDateString('de-DE')}
                                          </div>
                                        </div>
                                        <Check className="h-5 w-5 text-green-600" />
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <div className="text-right">
                                          <div className={`text-xs font-medium ${
                                            status.color === 'red' ? 'text-red-600' :
                                            status.color === 'orange' ? 'text-orange-600' :
                                            'text-gray-600'
                                          }`}>
                                            {status.label}
                                          </div>
                                          {item.deadline && (
                                            <div className="text-xs text-gray-500">
                                              {new Date(item.deadline).toLocaleDateString('de-DE')}
                                            </div>
                                          )}
                                        </div>
                                        <X className={`h-5 w-5 ${
                                          status.color === 'red' ? 'text-red-600' :
                                          status.color === 'orange' ? 'text-orange-600' :
                                          'text-gray-400'
                                        }`} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Resend Modal */}
      {resendModal && (
        <ResendInstructionModal
          instruction={resendModal}
          onClose={() => setResendModal(null)}
          onSuccess={() => {
            const inst = resendModal;
            setResendModal(null);
            fetchData();
            setSlSignModal({ instruction: inst });
          }}
        />
      )}

      {/* SL/Admin Unterschrift nach Versenden */}
      {slSignModal && (
        <InstructionSignModal
          open={!!slSignModal}
          onClose={() => setSlSignModal(null)}
          title={slSignModal.instruction?.title}
          signerLabel="Stationsleitung / Admin"
          signerName={slName}
          confirmLabel="Unterschrift speichern"
          isEmployee={false}
          onSign={async (sig) => {
            const token = localStorage.getItem('token');
            await axios.put(
              `${API}/api/instructions/${slSignModal.instruction.id}/sign-sl`,
              { signature_sl: sig },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Unterschrift gespeichert');
            setSlSignModal(null);
            fetchData();
          }}
        />
      )}
    </UnterweisungenLayout>
  );
};
